/**
 * Line Four's bands on the green and which columns are full, free of React
 * Native so node tests, the TV legends and the TV page's fixtures share them.
 */
import type { PuttSceneRolloutBand, PuttSceneStartLineWedge } from '../../../../packages/putt-scene/src/SceneContent';
import { BLACKOUT_BAND_COLOR, blackoutRolloutBand } from '../../../../packages/putt-scene/src/bandStates';
import {
  LINE_FOUR_BANDS,
  LINE_FOUR_COLUMNS,
  lowestOpenLineFourRow,
  type LineFourBoard,
} from './lineFour';
import { ADULT_DISTANCES, type DistanceView } from './kidDistances';

/** colors.goldLight (theme/tokens.ts, which imports React Native). */
const GOLD_LIGHT = '#DBA94D';
const LABEL_WHITE = '#FFFFFF';

/** Per column, true once it has no open row. A malformed board reads as all open. */
export function lineFourFullColumns(board: LineFourBoard | null | undefined): boolean[] {
  return Array.from({ length: LINE_FOUR_COLUMNS }, (_, column) =>
    Array.isArray(board) && board.length > 0 && board.every((row) => Array.isArray(row))
      ? lowestOpenLineFourRow(board, column) === null
      : false
  );
}

/**
 * The column letter a band stands for: "A" (or "A · 15-20 FT" from a phone
 * built on 2026-10-18). Null for anything else.
 */
export function lineFourBandLetter(label: string): string | null {
  const letter = label.split(' · ')[0]?.trim() ?? '';
  return /^[A-G]$/.test(letter) ? letter : null;
}

/** The right rail's text: the zone's first distance (Cole, 2026-10-19: "Just put the first distance"). */
export function lineFourRightLabel(fromFt: number): string {
  return String(fromFt);
}

/**
 * One band per column, gold cross-lines. Its letter is painted flat on the
 * turf on the left and its first distance flat on the right, both white and
 * centered in the zone, so each label plainly sits inside the zone it names
 * (Cole, 2026-11-01: "lay the labels down, so it's very clear which one is in
 * which zone"; they stood beside the green from 2026-10-18). A full column is
 * blacked out and both labels are removed (2026-10-18).
 */
export function lineFourRolloutBands(board: LineFourBoard | null | undefined, view: DistanceView = ADULT_DISTANCES): PuttSceneRolloutBand[] {
  const full = lineFourFullColumns(board);
  return LINE_FOUR_BANDS.map((band) => {
    // A Rookie or Pro turn draws its level's columns (kidModes.ts LINE_FOUR_LEVEL_MAPS).
    const shown: PuttSceneRolloutBand = {
      id: band.label,
      minFt: view.ft(band.fromFt),
      maxFt: view.ft(band.toFt),
      label: band.label,
      labelOrientation: 'flat',
      rightLabel: lineFourRightLabel(view.ft(band.fromFt)),
      labelColor: LABEL_WHITE,
      lineColor: GOLD_LIGHT,
    };
    return full[band.column] ? blackoutRolloutBand(shown) : shown;
  });
}

/** The start-line variant's fan with a full column's slice blacked out. */
export function lineFourStartLineWedges(
  wedges: readonly PuttSceneStartLineWedge[],
  board: LineFourBoard | null | undefined,
): PuttSceneStartLineWedge[] {
  const full = lineFourFullColumns(board);
  return wedges.map((wedge, column) => (full[column] ? { ...wedge, color: BLACKOUT_BAND_COLOR, opacity: 0.7 } : wedge));
}
